// PRESENTATION ONLY — not business logic.
//
// The backend (/me/capabilities) decides WHICH nav items a role sees and in what
// base order. This file decides only HOW they are arranged visually: which icon
// each item gets and which functional section of an automotive-parts ERP it
// belongs to (Catalog, Sales, Purchasing, Warehouse, Finance, Administration).
//
// Re-grouping is a display concern, so it lives in the frontend. Permissions and
// item visibility remain 100% backend-driven — an item the backend stops sending
// disappears here too, and an item we don't recognise still renders (under "More"),
// so the backend can add menu entries without a frontend release.

import {
  Tag,
  LayoutDashboard,
  Package,
  PackageSearch,
  PackagePlus,
  Boxes,
  ShoppingCart,
  FileText,
  LayoutGrid,
  FileSearch,
  Users,
  Percent,
  ScanLine,
  Receipt,
  Wallet,
  Factory,
  ShoppingBag,
  AlertTriangle,
  GitCompare,
  ClipboardList,
  ScanText,
  Undo2,
  Warehouse,
  Inbox,
  Archive,
  ListChecks,
  ClipboardCheck,
  CreditCard,
  FileMinus,
  FileSpreadsheet,
  Landmark,
  Shield,
  UserCog,
  Table2,
  SlidersHorizontal,
  ScrollText,
  FolderTree,
  BarChart3,
  FileCheck2,
  Calculator,
  Activity,
  Circle,
  type LucideIcon,
  PackageCheck,
  PhoneCall,
  Siren as SirenIcon,
  Banknote,
  Coins,
  MailWarning,
  Contact,
  Settings2,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import type { NavItem } from './navigation';

export type NavSectionKey =
  | 'overview'
  | 'catalog'
  | 'sales'
  | 'purchasing'
  | 'warehouse'
  | 'finance'
  | 'admin'
  | 'more';

// Section order for the sidebar. Sections with no items are skipped. The visible names live in
// the i18n catalogue under nav.sections.* — this file owns the order, not the wording.
export const NAV_SECTIONS: NavSectionKey[] = [
  'overview',
  'catalog',
  'sales',
  'purchasing',
  'warehouse',
  'finance',
  'admin',
  'more',
];

// Icon of each section when it is drawn as a collapsible group (and in the icon rail).
export const SECTION_ICONS: Record<NavSectionKey, LucideIcon> = {
  overview: LayoutDashboard,
  catalog: Package,
  sales: ShoppingCart,
  purchasing: ShoppingBag,
  warehouse: Warehouse,
  finance: Receipt,
  admin: Shield,
  more: Circle,
};

interface NavMeta {
  icon: LucideIcon;
  section: NavSectionKey;
}

// Exact-path metadata for every item the backend ships today (see
// backend/src/config/navigation.ts). Unknown paths fall back to prefix rules.
const PATH_META: Record<string, NavMeta> = {
  '/dashboard': { icon: LayoutDashboard, section: 'overview' },
  '/action-queue': { icon: ListChecks, section: 'more' },

  '/products': { icon: Package, section: 'catalog' },
  '/products/categories': { icon: FolderTree, section: 'catalog' },
  '/catalog': { icon: PackageSearch, section: 'catalog' },

  '/orders': { icon: ShoppingCart, section: 'sales' },
  '/quotes': { icon: FileText, section: 'sales' },
  '/customers': { icon: Users, section: 'sales' },
  '/admin/customers': { icon: Users, section: 'sales' },
  '/discounts': { icon: Percent, section: 'sales' },
  // The unified document register spans quotes → orders → BLs → invoices, so it sits with the
  // sales paperwork it searches rather than under Finance.
  '/documents': { icon: FileSearch, section: 'sales' },
  '/returns': { icon: Undo2, section: 'sales' },
  // Returns v2 (migration 150): the pages around the list (reception/validations are its tabs).
  '/returns/suppliers': { icon: Undo2, section: 'purchasing' },
  '/returns/money': { icon: FileMinus, section: 'finance' },
  '/returns/rules': { icon: SlidersHorizontal, section: 'admin' },
  // The B2B portal in the shared menu column (feedback 3.4).
  '/b2b/dashboard': { icon: LayoutDashboard, section: 'overview' },
  '/b2b/catalog': { icon: PackageSearch, section: 'catalog' },
  '/b2b/orders': { icon: ShoppingCart, section: 'sales' },
  '/b2b/invoices': { icon: Receipt, section: 'finance' },
  '/b2b/account': { icon: Contact, section: 'admin' },
  '/b2b/users': { icon: UserCog, section: 'admin' },
  '/b2b/returns': { icon: Undo2, section: 'sales' },
  '/b2b/returns/new': { icon: Undo2, section: 'sales' },
  '/b2b/returns/credits': { icon: FileMinus, section: 'finance' },
  '/refunds': { icon: Banknote, section: 'finance' },
  '/pos/sale': { icon: ScanLine, section: 'sales' },
  '/pos/tickets': { icon: Receipt, section: 'sales' },
  '/pos/close': { icon: Wallet, section: 'sales' },
  '/pos/sessions': { icon: ClipboardCheck, section: 'sales' },

  '/suppliers': { icon: Factory, section: 'purchasing' },
  '/admin/suppliers': { icon: Factory, section: 'purchasing' },
  '/purchasing': { icon: ShoppingBag, section: 'purchasing' },
  '/purchasing/alerts': { icon: AlertTriangle, section: 'purchasing' },
  '/purchasing/compare': { icon: GitCompare, section: 'purchasing' },
  '/purchasing/replenishment': { icon: PackagePlus, section: 'purchasing' },
  '/purchasing/orders': { icon: ClipboardList, section: 'purchasing' },
  '/purchasing/purchase-import': { icon: FileText, section: 'purchasing' },
  '/purchasing/invoices': { icon: ScanText, section: 'purchasing' },
  '/purchasing/returns': { icon: Undo2, section: 'purchasing' },

  '/stock': { icon: Boxes, section: 'warehouse' },
  // Grouped with the warehouse, not with purchasing, despite the /purchasing/* URL: the URL
  // reflects which router owns the endpoint, the sidebar reflects whose job it is. Loading
  // opening stock and posting counts is warehouse work, and it belongs next to Stock.
  '/purchasing/stock-import': { icon: FileSpreadsheet, section: 'warehouse' },
  '/warehouse': { icon: Warehouse, section: 'warehouse' },
  '/warehouse/dashboard': { icon: ClipboardList, section: 'warehouse' },
  '/warehouse/reception': { icon: Inbox, section: 'warehouse' },
  '/warehouse/ean-prep': { icon: ScanLine, section: 'warehouse' },
  '/warehouse/bins': { icon: PackageCheck, section: 'warehouse' },
  '/counter/call-list': { icon: PhoneCall, section: 'sales' },
  '/anomalies': { icon: SirenIcon, section: 'warehouse' },
  '/warehouse/shelving': { icon: Archive, section: 'warehouse' },
  '/warehouse/picking': { icon: ListChecks, section: 'warehouse' },
  '/warehouse/shipping': { icon: ShoppingCart, section: 'warehouse' },
  '/warehouse/inventory': { icon: ClipboardCheck, section: 'warehouse' },

  '/invoices': { icon: Receipt, section: 'finance' },
  '/recap-invoices': { icon: FileSpreadsheet, section: 'finance' },
  '/remittance': { icon: Landmark, section: 'finance' },
  '/recap-tracking': { icon: Landmark, section: 'finance' },
  '/credit-notes': { icon: FileMinus, section: 'finance' },
  '/payments': { icon: CreditCard, section: 'finance' },
  // Migration 151 — the Recouvrement prototype.
  '/collection': { icon: Coins, section: 'finance' },
  '/collection/payments': { icon: CreditCard, section: 'finance' },
  '/collection/dunning': { icon: MailWarning, section: 'finance' },
  '/collection/accounts': { icon: Contact, section: 'finance' },
  '/collection/settings': { icon: Settings2, section: 'finance' },
  '/accounting/exports': { icon: FileSpreadsheet, section: 'finance' },
  '/einvoice': { icon: FileCheck2, section: 'finance' },
  '/einvoice/vat': { icon: Calculator, section: 'finance' },
  '/einvoice/monitoring': { icon: Activity, section: 'finance' },
  '/reports': { icon: BarChart3, section: 'finance' },

  '/admin': { icon: Shield, section: 'admin' },
  '/admin/users': { icon: UserCog, section: 'admin' },
  '/admin/pricing-grids': { icon: Table2, section: 'finance' },
  '/admin/brands': { icon: Tag, section: 'admin' },
  '/admin/settings': { icon: SlidersHorizontal, section: 'admin' },
  '/admin/document-templates': { icon: FileText, section: 'admin' },
  '/admin/counter-tabs': { icon: LayoutGrid, section: 'admin' },
  '/admin/audit': { icon: ScrollText, section: 'admin' },
};

// Prefix fallbacks for paths not in PATH_META (longest prefix wins).
const PREFIX_META: { prefix: string; meta: NavMeta }[] = [
  { prefix: '/warehouse', meta: { icon: Warehouse, section: 'warehouse' } },
  { prefix: '/purchasing', meta: { icon: ShoppingBag, section: 'purchasing' } },
  { prefix: '/pos', meta: { icon: ScanLine, section: 'sales' } },
  { prefix: '/accounting', meta: { icon: FileSpreadsheet, section: 'finance' } },
  { prefix: '/einvoice', meta: { icon: FileCheck2, section: 'finance' } },
  { prefix: '/reports', meta: { icon: BarChart3, section: 'finance' } },
  { prefix: '/admin', meta: { icon: Shield, section: 'admin' } },
  { prefix: '/stock', meta: { icon: Boxes, section: 'warehouse' } },
];

export function navMetaFor(path: string): NavMeta {
  const exact = PATH_META[path];
  if (exact) return exact;
  const byPrefix = PREFIX_META.filter(
    (p) => path === p.prefix || path.startsWith(p.prefix + '/')
  ).sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return byPrefix?.meta ?? { icon: Circle, section: 'more' };
}

export interface NavSection {
  key: NavSectionKey;
  items: (NavItem & { icon: LucideIcon })[];
}

/** Group the backend-supplied items into ordered sections, preserving backend
 *  order within each section. Empty sections are dropped. */
export function buildNavSections(items: NavItem[]): NavSection[] {
  const decorated = items.map((it) => ({ ...it, ...navMetaFor(it.path) }));
  return NAV_SECTIONS.map((key) => ({
    key,
    items: decorated.filter((it) => it.section === key),
  })).filter((s) => s.items.length > 0);
}

/** The active item is the one whose path is the longest prefix of the current
 *  location — so /warehouse/reception/123 still highlights "Receptions". */
export function activeItem(items: NavItem[], pathname: string): NavItem | undefined {
  return items
    .filter((it) => pathname === it.path || pathname.startsWith(it.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

/**
 * Translate one backend-supplied menu label.
 *
 * Keyed on the English label rather than on the path, because the backend deliberately ships the
 * SAME path under different wording per role (/b2b/orders is "My Orders" for a buyer and
 * "Orders (read)" for a viewer) — a path key would flatten that distinction. An item with no
 * entry in the catalogue renders its English label unchanged, so the backend can still add a
 * menu entry without a frontend release.
 */
export function navLabel(label: string, t: TFunction): string {
  return t(`nav.items.${label}`, { defaultValue: label });
}

/** Catalogue entry point for the global parts search, if the role has one. */
export function catalogPath(items: NavItem[]): string | undefined {
  return items.find((it) => it.path === '/products' || it.path === '/catalog')?.path;
}
